/**
 * Логотип проекта.
 *
 * Пока логотип не загружен, рисуется буквенный значок — та же логика, что у
 * аватара без фотографии: пустой прямоугольник хуже, чем узнаваемая заглушка.
 *
 * Скругление, а не круг: логотип проекта чаще всего прямоугольный знак или
 * слово, и круглая маска срезала бы у него углы. Квадрат со скруглением
 * сохраняет форму и при этом не спорит с круглыми аватарами рядом.
 */

import type { ProjectSummaryDto } from '@project-os/contracts';
import { mediaUrl } from '@/api/client';
import { cn } from '@/lib/utils';

const SIZES = {
  sm: 'size-5 rounded text-[8px]',
  md: 'size-6 rounded-md text-[9px]',
  lg: 'size-8 rounded-md text-[11px]',
  xl: 'size-12 rounded-lg text-base',
} as const;

/** Первые буквы слов названия: «Berlin Rental Assistant» даёт «BR». */
function projectInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '—';
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

export function ProjectLogo({
  project,
  size = 'md',
  className,
}: {
  project: Pick<ProjectSummaryDto, 'name' | 'logoUrl'>;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  if (project.logoUrl) {
    return (
      <img
        src={mediaUrl(project.logoUrl)}
        alt=""
        // object-contain, а не cover: логотип нужно показать целиком, а не
        // кадрировать. Обрезанный логотип перестаёт быть логотипом.
        className={cn('shrink-0 bg-muted/40 object-contain', SIZES[size], className)}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        'grid shrink-0 place-items-center bg-primary font-bold text-primary-foreground',
        SIZES[size],
        className,
      )}
    >
      {projectInitials(project.name)}
    </span>
  );
}
